/**
 * Contenu éditorial du site vitrine « Bright Dental LAB ».
 * Rédigé pour un public de praticiens et cliniques dentaires — jamais de patients.
 * Source unique réutilisée par la page d'accueil et les pages internes.
 */
import {
  Crown,
  Component,
  Gem,
  CircleDot,
  Smile,
  Layers,
  Cpu,
  Printer,
  Sparkles,
  Microscope,
  ShieldCheck,
  Clock,
  Workflow,
  MessageSquare,
  Award,
  Boxes,
  ScanLine,
  Bell,
  FileText,
  Upload,
  Truck,
  type LucideIcon,
} from 'lucide-react';

export interface ServiceItem {
  slug: string;
  title: string;
  description: string;
  benefits: string[];
  icon: LucideIcon;
}

export const SERVICES: ServiceItem[] = [
  {
    slug: 'couronne',
    title: 'Couronne',
    description:
      'Esthétique supérieure et grande résistance pour les dents antérieures et postérieures, avec une intégration parfaite.',
    benefits: ['Esthétique supérieure', 'Grande résistance', 'Ajustage précis'],
    icon: Crown,
  },
  {
    slug: 'bridge',
    title: 'Bridge',
    description:
      'Solution fiable pour remplacer plusieurs dents manquantes avec un résultat naturel et une stabilité durable.',
    benefits: ['Remplacement fixe', 'Résultat naturel', 'Charges maîtrisées'],
    icon: Component,
  },
  {
    slug: 'inlay-onlay',
    title: 'Inlay / Onlay',
    description:
      'Restaurations partielles sur-mesure pour conserver au maximum la structure dentaire saine tout en restaurant la fonction.',
    benefits: ['Préservation tissulaire', 'Ajustage sur-mesure', 'Longévité'],
    icon: Layers,
  },
  {
    slug: 'implant',
    title: 'Prothèse sur implant',
    description:
      'Fixations personnalisées pour des restaurations implantaires durables et précises, en solutions vissées ou scellées.',
    benefits: ['Passivité d’assemblage', 'Flux full-digital', 'Durabilité'],
    icon: CircleDot,
  },
  {
    slug: 'facette',
    title: 'Facette dentaire',
    description:
      'Facettes ultra-fines en céramique pour transformer l’esthétique du sourire avec une grande finesse et un rendu naturel.',
    benefits: ['Épaisseur minimale', 'Translucidité maîtrisée', 'Rendu sur-mesure'],
    icon: Gem,
  },
  {
    slug: 'amovible-partielle',
    title: 'Amovible partielle',
    description:
      'Prothèses partielles alliant confort, fonction et esthétique pour remplacer plusieurs dents absentes.',
    benefits: ['Confort optimisé', 'Maintien fiable', 'Intégration esthétique'],
    icon: Smile,
  },
  {
    slug: 'amovible-complete',
    title: 'Amovible complète',
    description:
      'Prothèses complètes réalisées selon des protocoles numériques précis, pour une fonction et une esthétique restaurées.',
    benefits: ['Montage fonctionnel', 'Confort durable', 'Finitions soignées'],
    icon: Smile,
  },
  {
    slug: 'gouttiere',
    title: 'Gouttière',
    description:
      'Gouttières sur-mesure (protection, bruxisme, contention) fabriquées avec précision pour un port confortable.',
    benefits: ['Ajustage précis', 'Port confortable', 'Matériau durable'],
    icon: ShieldCheck,
  },
];

export interface MaterialItem {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  strength: string;
  esthetics: string;
  applications: string[];
}

export const MATERIALS: MaterialItem[] = [
  {
    slug: 'zircone',
    name: 'Zircone',
    tagline: 'Résistance & polyvalence',
    description:
      'Céramique polycristalline offrant une résistance mécanique exceptionnelle, idéale des restaurations postérieures aux bridges de grande portée.',
    strength: '> 1200 MPa',
    esthetics: 'Multicouche translucide',
    applications: ['Couronnes postérieures', 'Bridges longue portée', 'Piliers implantaires'],
  },
  {
    slug: 'disilicate-lithium',
    name: 'Disilicate de lithium',
    tagline: 'Esthétique de référence',
    description:
      'Vitrocéramique haute résistance conjuguant translucidité remarquable et solidité, référence des restaurations antérieures.',
    strength: '≈ 400 MPa',
    esthetics: 'Translucidité premium',
    applications: ['Facettes', 'Couronnes antérieures', 'Inlays / onlays'],
  },
  {
    slug: 'ceramique-feldspathique',
    name: 'Céramique feldspathique',
    tagline: 'Finesse & naturel',
    description:
      'Céramique de stratification permettant les rendus les plus naturels grâce à un travail de la lumière et des teintes en couches successives.',
    strength: 'Stratification fine',
    esthetics: 'Rendu ultra-naturel',
    applications: ['Facettes fines', 'Stratification esthétique', 'Cas antérieurs'],
  },
  {
    slug: 'ceramique-hybride',
    name: 'Céramique hybride',
    tagline: 'Équilibre & amorti',
    description:
      'Matériau hybride combinant céramique et résine pour un amorti fonctionnel et un usinage précis, adapté aux restaurations conservatrices.',
    strength: 'Module amortissant',
    esthetics: 'Aspect maîtrisé',
    applications: ['Restaurations conservatrices', 'Provisoires longue durée', 'Usinage rapide'],
  },
  {
    slug: 'resine',
    name: 'Résines techniques',
    tagline: 'Prototypage & provisoires',
    description:
      'Résines d’impression et d’usinage de haute précision pour modèles, guides chirurgicaux et restaurations provisoires fiables.',
    strength: 'Stabilité dimensionnelle',
    esthetics: 'Finitions nettes',
    applications: ['Modèles de travail', 'Guides chirurgicaux', 'Provisoires'],
  },
];

export interface StatItem {
  value: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  label: string;
}

// NOTE : valeurs indicatives — à ajuster avec les chiffres réels du laboratoire.
export const STATS: StatItem[] = [
  { value: 12, suffix: '+', label: 'Années d’expertise' },
  { value: 85000, suffix: '+', label: 'Restaurations livrées' },
  { value: 420, suffix: '+', label: 'Cliniques partenaires' },
  { value: 99.4, decimals: 1, suffix: '%', label: 'Taux de conformité' },
];

export interface ReasonItem {
  title: string;
  description: string;
  icon: LucideIcon;
}

export const REASONS: ReasonItem[] = [
  {
    title: 'Expertise artisanale',
    description:
      'Des prothésistes hautement qualifiés, garants d’un savoir-faire d’exception à chaque étape.',
    icon: Award,
  },
  {
    title: 'Précision numérique',
    description:
      'Un flux CAO/FAO maîtrisé pour un ajustage micrométrique et une reproductibilité totale.',
    icon: ScanLine,
  },
  {
    title: 'Technologies de pointe',
    description:
      'Usinage, frittage et impression 3D dernière génération au service de la qualité.',
    icon: Cpu,
  },
  {
    title: 'Matériaux premium',
    description:
      'Une sélection rigoureuse de matériaux certifiés issus des meilleurs fabricants mondiaux.',
    icon: Gem,
  },
  {
    title: 'Contrôle qualité',
    description:
      'Un protocole d’inspection systématique et une traçabilité complète de chaque pièce.',
    icon: ShieldCheck,
  },
  {
    title: 'Délais maîtrisés',
    description:
      'Une organisation industrielle fiable garantissant des délais courts et respectés.',
    icon: Clock,
  },
  {
    title: 'Flux 100 % numérique',
    description:
      'De l’empreinte optique à la livraison, un parcours fluide et sans ressaisie.',
    icon: Workflow,
  },
  {
    title: 'Accompagnement dédié',
    description:
      'Un interlocuteur à votre écoute et un support réactif pour chaque cas confié.',
    icon: MessageSquare,
  },
];

export interface ProcessStep {
  index: string;
  title: string;
  description: string;
  icon: LucideIcon;
}

/** Parcours de A à Z — le praticien reste l'unique interlocuteur (jamais de patient). */
export const PROCESS_STEPS: ProcessStep[] = [
  {
    index: '01',
    title: 'Préparation du cas',
    description: 'Le praticien prépare le cas et réalise l’empreinte optique ou physique.',
    icon: ScanLine,
  },
  {
    index: '02',
    title: 'Transmission numérique',
    description: 'Fichiers STL, photographies et prescription sont transmis via la plateforme.',
    icon: Upload,
  },
  {
    index: '03',
    title: 'Réception & analyse',
    description: 'Le laboratoire réceptionne la commande et procède à l’analyse numérique du cas.',
    icon: Cpu,
  },
  {
    index: '04',
    title: 'Conception CAO',
    description: 'La restauration est modélisée avec précision puis validée avant fabrication.',
    icon: Sparkles,
  },
  {
    index: '05',
    title: 'Fabrication',
    description: 'Usinage, frittage, stratification et finitions par nos prothésistes experts.',
    icon: Boxes,
  },
  {
    index: '06',
    title: 'Contrôle qualité',
    description: 'Inspection rigoureuse de l’ajustage, de la teinte et de l’occlusion.',
    icon: Microscope,
  },
  {
    index: '07',
    title: 'Livraison',
    description: 'La restauration est expédiée au cabinet, prête à être posée en toute confiance.',
    icon: Truck,
  },
];

export interface PlatformFeature {
  title: string;
  description: string;
  icon: LucideIcon;
}

export const PLATFORM_FEATURES: PlatformFeature[] = [
  {
    title: 'Commandes en ligne',
    description: 'Créez et transmettez vos commandes en quelques minutes, sans papier.',
    icon: FileText,
  },
  {
    title: 'Dépôt de fichiers STL',
    description: 'Transmettez vos empreintes numériques en toute sécurité.',
    icon: Upload,
  },
  {
    title: 'Dépôt de photographies',
    description: 'Joignez photos cliniques et références de teinte à chaque cas.',
    icon: ScanLine,
  },
  {
    title: 'Suivi des commandes',
    description: 'Visualisez l’avancement de chaque cas, étape par étape.',
    icon: Boxes,
  },
  {
    title: 'Gestion des livraisons',
    description: 'Anticipez les réceptions et planifiez vos rendez-vous en confiance.',
    icon: Truck,
  },
  {
    title: 'Notifications',
    description: 'Soyez informé à chaque changement de statut de votre commande.',
    icon: Bell,
  },
  {
    title: 'Messagerie intégrée',
    description: 'Échangez directement avec le laboratoire sur chaque dossier.',
    icon: MessageSquare,
  },
  {
    title: 'Historique des commandes',
    description: 'Retrouvez l’ensemble de vos cas passés, centralisés et consultables.',
    icon: Boxes,
  },
  {
    title: 'Factures',
    description: 'Accédez à vos documents comptables et suivez votre facturation.',
    icon: FileText,
  },
];

export const PLATFORM_BENEFITS: string[] = [
  'Gagnez du temps',
  'Réduisez les erreurs',
  'Suivez vos cas en temps réel',
  'Simplifiez la communication',
  'Gagnez en efficacité',
];

export interface Partner {
  name: string;
}

/** Marques partenaires de référence (fabricants de matériaux & solutions). */
export const PARTNERS: Partner[] = [
  { name: 'VITA' },
  { name: 'Ivoclar' },
  { name: 'GC' },
  { name: 'Noritake' },
  { name: 'Amber' },
  { name: 'Rosetta' },
  { name: 'REOX' },
  { name: 'Dental Direkt' },
];

export interface ValueItem {
  title: string;
  description: string;
  icon: LucideIcon;
}

export const VALUES: ValueItem[] = [
  {
    title: 'Excellence',
    description:
      'La recherche du détail parfait guide chacun de nos gestes, de la conception à la finition.',
    icon: Award,
  },
  {
    title: 'Précision',
    description:
      'Un ajustage micrométrique et une reproductibilité totale, garantis par le numérique.',
    icon: ScanLine,
  },
  {
    title: 'Innovation',
    description:
      'Nous investissons en continu dans les technologies qui repoussent les standards du métier.',
    icon: Sparkles,
  },
  {
    title: 'Intégrité',
    description:
      'Traçabilité, matériaux certifiés et transparence : la confiance se construit sur des preuves.',
    icon: ShieldCheck,
  },
  {
    title: 'Collaboration',
    description:
      'Nous travaillons main dans la main avec les praticiens, comme un prolongement de leur cabinet.',
    icon: MessageSquare,
  },
];

// NOTE : coordonnées de démonstration — à remplacer par les informations réelles.
export const CONTACT_INFO = {
  email: 'contact@brightdentallab.com',
  phone: '+33 (0)1 84 80 00 00',
  address: '12 Avenue de l’Innovation, 75008 Paris, France',
};

export const BUSINESS_HOURS: { day: string; hours: string }[] = [
  { day: 'Lundi – Vendredi', hours: '08h30 – 18h30' },
  { day: 'Samedi', hours: '09h00 – 13h00' },
  { day: 'Dimanche', hours: 'Fermé' },
];
